import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import axios from 'axios';
import { API_CONFIG } from '../utils/apiConfig';
import { shouldRecordPunchingTime } from '../utils/punchingTimeUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Function to record automatic punch-in
const recordAutomaticPunchIn = async (user, userProfile) => {
  // Skip punching time functionality if not on localhost
  if (API_CONFIG.BASE_URL.includes('api-aovv3tvopa-uc.a.run.app')) {
    return;
  }
  
  try {
    const idToken = await user.getIdToken(true);
    const headers = { Authorization: `Bearer ${idToken}` };
    
    // First check if user already has an active session
    try {
      const currentStatusRes = await axios.get(`${API_CONFIG.BASE_URL}/api/punching-times/current`, { 
        headers,
        timeout: 5000 // Add timeout to prevent hanging
      });
      if (currentStatusRes.data) {
        console.log('🔍 User already has an active session, skipping punch-in');
        return; // User already punched in
      }
    } catch (statusErr) {
      // If 404 error, the endpoint doesn't exist - skip punching functionality
      if (statusErr.response?.status === 404) {
        return;
      }
      // For network errors, skip punching functionality silently
      if (statusErr.code === 'ERR_NETWORK' || statusErr.message === 'Network Error') {
        console.log('🔇 Network error - skipping punching time functionality');
        return;
      }
      // For other errors, proceed with punch-in
    }
    
    try {
      const punchInData = {
        email: user.email,
        role: userProfile?.role || 'admin', // Default to 'admin' for digital marketing admin
        name: userProfile?.name || userProfile?.displayName || user.displayName || 'Unknown User',
        department: userProfile?.department || null
      };
      
      console.log('🔍 Punch-in data being sent:', punchInData);
      console.log('🔍 User profile:', userProfile);
      
      await axios.post(
        `${API_CONFIG.BASE_URL}/api/punching-times/record-login`,
        punchInData,
        { 
          headers,
          timeout: 5000 // Add timeout to prevent hanging
        }
      );
    } catch (punchErr) {
      // If 404 error, the endpoint doesn't exist - skip punching functionality
      if (punchErr.response?.status === 404) {
        return;
      }
      // For network errors, skip punching functionality silently
      if (punchErr.code === 'ERR_NETWORK' || punchErr.message === 'Network Error') {
        console.log('🔇 Network error - skipping punching time functionality');
        return;
      }
      // Log the actual error response for debugging
      if (punchErr.response?.data) {
        console.error('❌ Punch-in API error response:', punchErr.response.data);
        
        // Handle the case where user already has an active session
        if (punchErr.response.data.error && punchErr.response.data.error.includes('already has an active punching session')) {
          console.log('🔍 User already has an active session, skipping punch-in');
          return; // Don't throw error, just skip
        }
        // Handle successful session update
        if (punchErr.response.data.message && punchErr.response.data.message.includes('Session updated')) {
          console.log('🔍 Session updated successfully:', punchErr.response.data);
          return; // Don't throw error, session was updated
        }
      }
      console.error('❌ Punch-in API error:', punchErr.message);
      throw punchErr; // Re-throw other errors
    }
    
  } catch (error) {
    // Handle network errors gracefully
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.log('🔇 Network error - skipping punching time functionality');
      return;
    }
    console.error('❌ Failed to record automatic punch-in:', error);
    // Don't throw error to avoid breaking login flow
  }
};

// Function to record automatic punch-out
const recordAutomaticPunchOut = async (user) => {
  // Skip punching time functionality if not on localhost
  if (API_CONFIG.BASE_URL.includes('api-aovv3tvopa-uc.a.run.app')) {
    return;
  }
  
  // Only record punch-out for roles that should be tracked
  const role = localStorage.getItem('role');
  if (!shouldRecordPunchingTime(role)) {
    console.log('🔇 Role not tracked for punching time:', role);
    return;
  }
  
  try {
    const idToken = await user.getIdToken(true);
    const headers = { Authorization: `Bearer ${idToken}` };
    
    // First check if user has an active session
    try {
      const currentSessionResponse = await axios.get(
        `${API_CONFIG.BASE_URL}/api/punching-times/current`,
        { headers, timeout: 3000 }
      );
      
      if (!currentSessionResponse.data) {
        console.log('🔇 No active punching session found - skipping punch-out');
        return;
      }
    } catch (sessionCheckError) {
      // If we can't check for current session, proceed with punch-out attempt
      console.log('🔇 Could not check current session, proceeding with punch-out');
    }
    
    await axios.post(
      `${API_CONFIG.BASE_URL}/api/punching-times/record-logout`,
      {
        email: user.email
      },
      { 
        headers,
        timeout: 5000 // Add timeout to prevent hanging
      }
    );
    
  } catch (error) {
    // Handle network errors gracefully
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.log('🔇 Network error - skipping punch-out functionality');
      return;
    }
    
    // Handle specific HTTP errors gracefully
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.error || error.message;
      
      if (status === 404) {
        console.log('🔇 No active punching session found - user may have already logged out');
        return;
      } else if (status === 400) {
        console.log('🔇 Bad request for punch-out:', message);
        return;
      } else {
        console.log('🔇 Punch-out API error:', status, message);
        return;
      }
    }
    
    console.error('❌ Failed to record automatic punch-out:', error);
    // Don't throw error to avoid breaking logout flow
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isRestoring, setIsRestoring] = useState(true);

  // Sign up with email and password
  const signup = async (email, password, userData) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile
      await updateProfile(result.user, {
        displayName: userData.displayName || userData.name,
        photoURL: userData.photoURL
      });

      // Save additional user data to Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        ...userData,
        email,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        isActive: true
      });

      return result;
    } catch (error) {
      throw error;
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      // Ensure we have a fresh ID token for backend verification
      const idToken = await result.user.getIdToken(true);
      
      // Update last login time
      await updateDoc(doc(db, 'users', result.user.uid), {
        lastLogin: new Date().toISOString()
      });

      // Get user profile for punch-in recording
      const profile = await getUserProfile(result.user.uid);
      
      // Set user profile in state
      setUserProfile(profile);
      
      // Record automatic punch-in
      await recordAutomaticPunchIn(result.user, profile);

      return { ...result, idToken };
    } catch (error) {
      throw error;
    }
  };

// Sign in with Google
const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const res = await signInWithPopup(auth, provider);
    const idToken = await res.user.getIdToken(true);
    
    const profile = await getUserProfile(res.user.uid);
    
    setUserProfile(profile);
    await recordAutomaticPunchIn(res.user, profile);
    
    return { ...res, idToken };
  } catch (error) {
    if (error.code === 'auth/popup-blocked' || error.code === 'auth/operation-not-supported-in-this-environment') {
      await signInWithRedirect(auth, provider);
      // getRedirectResult should be handled after redirect on app load if needed
      return null;
    }
    throw error;
  }
};

  const logout = async () => {
    try {
      // Record automatic punch-out before signing out
      if (currentUser) {
        await recordAutomaticPunchOut(currentUser);
      }
      
      // Clear all authentication-related localStorage items
      localStorage.removeItem('firebase:authUser:wyenfosbills:[DEFAULT]');
      localStorage.removeItem('token');
      localStorage.removeItem('email');
      localStorage.removeItem('role');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('dept');
      
      // Clear cached user profiles
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('userProfile_')) {
          localStorage.removeItem(key);
        }
      });
      
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };

  // Get user profile from Firestore
  const getUserProfile = async (uid) => {
    // Check if we should use localhost API instead of Firebase
    const useLocalhostAPI = localStorage.getItem('forceLocalhostAPI') === 'true' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
    
    if (useLocalhostAPI) {
      console.log('🔄 Using Firebase Functions API for user profile');
      try {
        // Get fresh token from current user
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('No authenticated user');
        }
        const idToken = await currentUser.getIdToken();
        
        // Store token in localStorage for other components to use
        localStorage.setItem('token', idToken);
        
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/${uid}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          localStorage.setItem(`userProfile_${uid}`, JSON.stringify(userData));
          return userData;
        } else {
          console.log('❌ Localhost API failed, using fallback');
        }
    } catch (error) {
      console.log('❌ Localhost API error:', error.message);
    }
  }
  
  // Try to get cached profile as fallback
  const cachedProfile = localStorage.getItem(`userProfile_${uid}`);
  if (cachedProfile) {
    console.log('🔄 Using cached profile as fallback');
    return JSON.parse(cachedProfile);
  }
  
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const userData = { id: docSnap.id, ...docSnap.data() };
      // Cache the profile for offline use
      localStorage.setItem(`userProfile_${uid}`, JSON.stringify(userData));
      return userData;
    } else {
      return null;
    }
  } catch (error) {
      console.error('Error getting user profile:', error);
      
      // Handle offline mode gracefully
      if (error.code === 'unavailable' || error.message.includes('offline') || error.message.includes('Failed to get document') || error.message.includes('Could not reach Cloud Firestore backend')) {
        console.log('🔄 Firebase is offline, using cached data');
        
        // Try to get cached profile first
        const cachedProfile = localStorage.getItem(`userProfile_${uid}`);
        if (cachedProfile) {
          try {
            const profile = JSON.parse(cachedProfile);
            profile.isOffline = true;
            profile.lastCached = new Date().toISOString();
            console.log('✅ Using cached profile for offline mode');
            return profile;
          } catch (parseError) {
            console.log('⚠️ Cached profile corrupted, using default');
          }
        }
        
        // Return a basic offline profile
        const offlineProfile = {
          id: uid,
          name: 'User',
          email: 'user@example.com',
          role: 'user',
          isOffline: true,
          lastCached: new Date().toISOString()
        };
        
        // Cache this offline profile
        localStorage.setItem(`userProfile_${uid}`, JSON.stringify(offlineProfile));
        console.log('✅ Created offline profile');
        return offlineProfile;
      }
      return null;
    }
  };

  // Update user profile
  const updateUserProfile = async (uid, updates) => {
    try {
      await updateDoc(doc(db, 'users', uid), updates);
      // Refresh user profile
      const updatedProfile = await getUserProfile(uid);
      setUserProfile(updatedProfile);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    // Check for cached authentication state first
    const cachedUser = localStorage.getItem('firebase:authUser:wyenfosbills:[DEFAULT]');
    let initTimer;
    
    if (cachedUser) {
      console.log('Found cached authentication state');
      // If we have cached auth, wait a bit longer for Firebase to restore
      initTimer = setTimeout(() => {
        setIsRestoring(false);
      }, 500);
    } else {
      // No cached auth, can proceed faster
      initTimer = setTimeout(() => {
        setIsRestoring(false);
      }, 100);
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'User logged out');
      
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get fresh token and store it
          const idToken = await user.getIdToken();
          localStorage.setItem('token', idToken);
          
          // Try to get cached profile first to prevent redirects
          const cachedProfile = localStorage.getItem(`userProfile_${user.uid}`);
          if (cachedProfile) {
            const parsedProfile = JSON.parse(cachedProfile);
            setUserProfile(parsedProfile);
            // Store role and department for immediate access
            if (parsedProfile.role) {
              localStorage.setItem('userRole', parsedProfile.role);
              console.log('🔄 Stored role in localStorage:', parsedProfile.role);
            }
            if (parsedProfile.department) {
              localStorage.setItem('userDepartment', parsedProfile.department);
              console.log('🔄 Stored department in localStorage:', parsedProfile.department);
            }
            console.log('🔄 Using cached profile:', parsedProfile);
          }
          
          // Get fresh user profile from Firestore
          const profile = await getUserProfile(user.uid);
          if (profile) {
            setUserProfile(profile);
            // Store role and department for immediate access
            if (profile.role) {
              localStorage.setItem('userRole', profile.role);
              console.log('🔄 Updated role in localStorage:', profile.role);
            }
            if (profile.department) {
              localStorage.setItem('userDepartment', profile.department);
              console.log('🔄 Updated department in localStorage:', profile.department);
            }
            console.log('🔄 Updated with fresh profile:', profile);
          } else if (cachedProfile) {
            // Keep using cached profile if fresh fetch fails
            console.log('🔄 Keeping cached profile due to fetch failure');
          } else {
            // If no profile found, create a default one for admin users
            if (user.email && user.email.includes('admin')) {
              const defaultProfile = {
                id: user.uid,
                name: user.displayName || 'Admin User',
                email: user.email,
                role: 'admin',
                department: 'hr'
              };
              setUserProfile(defaultProfile);
              localStorage.setItem('userRole', 'admin');
              localStorage.setItem('userDepartment', 'hr');
              console.log('🔄 Created default admin profile:', defaultProfile);
            }
          }
          
          // Record automatic punch-in for new sessions
          // Only record if this is a fresh login (not just page refresh)
          const lastLogin = profile?.lastLogin;
          const now = new Date();
          const timeDiff = now - new Date(lastLogin || 0);
          
          // If last login was more than 5 minutes ago, consider it a new session
          if (!lastLogin || timeDiff > 5 * 60 * 1000) {
            await recordAutomaticPunchIn(user, profile);
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          // Set a basic profile to prevent redirect
          // Try to get role from localStorage as fallback
          const cachedRole = localStorage.getItem('userRole');
          const cachedDepartment = localStorage.getItem('userDepartment');
          
          setUserProfile({
            id: user.uid,
            name: user.displayName || 'User',
            email: user.email,
            role: cachedRole || 'user',
            department: cachedDepartment || 'general'
          });
        }
      } else {
        setUserProfile(null);
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userDepartment');
        // Clear all cached profiles
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('userProfile_')) {
            localStorage.removeItem(key);
          }
        });
      }
      
      setAuthInitialized(true);
      setLoading(false);
    });

    return () => {
      clearTimeout(initTimer);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getRedirectResult(auth);
        if (res?.user) {
          const user = res.user;
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (!userDoc.exists()) {
            await setDoc(doc(db, 'users', user.uid), {
              displayName: user.displayName,
              email: user.email,
              photoURL: user.photoURL,
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString(),
              isActive: true
            });
          } else {
            await updateDoc(doc(db, 'users', user.uid), {
              lastLogin: new Date().toISOString()
            });
          }
          
          // Record automatic punch-in for Google redirect
          const profile = await getUserProfile(user.uid);
          await recordAutomaticPunchIn(user, profile);
        }
      } catch (e) {
        console.error('Google redirect sign-in error:', e);
      }
    })();
  }, []);

  // Handle page unload/close to record punch-out
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (currentUser) {
        try {
          await recordAutomaticPunchOut(currentUser);
        } catch (error) {
          console.error('Failed to record punch-out on page unload:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentUser]);

  // Clear all authentication state (for debugging or force logout)
  const clearAuthState = () => {
    localStorage.removeItem('firebase:authUser:wyenfosbills:[DEFAULT]');
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('dept');
    
    // Clear cached user profiles
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('userProfile_')) {
        localStorage.removeItem(key);
      }
    });
    
    setCurrentUser(null);
    setUserProfile(null);
    setAuthInitialized(false);
  };

  // Debug function to check punching time status
  const debugPunchingStatus = async () => {
    if (!currentUser) {
      console.log('🔍 No current user for punching status check');
      return;
    }
    
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/api/punching-times/current`,
        { headers, timeout: 5000 }
      );
      
      console.log('🔍 Current punching session:', response.data);
      return response.data;
    } catch (error) {
      console.log('🔍 Punching status check failed:', error.response?.data || error.message);
      return null;
    }
  };

  const value = {
    currentUser,
    userProfile,
    setUserProfile,
    loading,
    authInitialized,
    isRestoring,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    updateUserProfile,
    getUserProfile,
    clearAuthState,
    debugPunchingStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
